import { Box, Button, IconButton, Typography, Grid } from '@mui/material';
import Menu from '@mui/material/Menu';
import HomeIcon from '@mui/icons-material/Home';
import MenuItem from '@mui/material/MenuItem';
import { MaterialReactTable, MRT_ColumnDef } from 'material-react-table';
import React, { useEffect, useMemo, useState } from 'react';
import {
  PaginatedResponse,
  FilterObj,
  RoleFormData,
} from '../../../interfaces/Management.interface.ts';
import SearchBox from '../../FlowBuilder/Common/SearchBox/index.tsx';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import ConfirmBox from '../../FlowBuilder/Common/ConfirmBox/index.tsx';
import { useSnackBar } from '../../FlowBuilder/Common/SnackBar/index.tsx';
import Pagination from '../../FlowBuilder/Common/Pagination/index.tsx';
import DynamicBreadcrumbs from '../../Shared/Breadcrumb/index.tsx';
import { useNavigate } from 'react-router';
import { useRecoilState, useRecoilValue, useRecoilValueLoadable } from 'recoil';
import OrganisationDropdown from '../../Shared/OrganisationDropdown/index.tsx';
import { userInformationAtom } from '../../../Services/_states/auth.js';
import useDebounce from '../../Shared/Delay/index.tsx';
import { EntityActions } from '../../../Services/_actions/Entity/entity.actions.ts';
import { EntityFormdata } from '../../../interfaces/Entity.interface.ts';
import {
  entityFormsStateAtomFamily,
  entityTableStateAtom,
  staticEntityFormData,
} from '../../../Services/_states/index.js';
import BusinessModal from './BusinessModal.tsx';
import { fetchEntityFormListSelector } from '../../../Services/_states/Entity/entitySelector.ts';
import { generateFilterKey } from '../../../Services/_states/Entity/entitySelector.ts';
import CommonTable, { EllipsisWithTooltip } from '../../Shared/Tables/TableV1.tsx';
import DeleteIcon from '@mui/icons-material/Delete';
import DescriptionIcon from '@mui/icons-material/Description';
import EditIcon from '@mui/icons-material/Edit';
import NoteAddIcon from '@mui/icons-material/NoteAdd';

const List: React.FC = () => {
  const actions = EntityActions();
  const navigate = useNavigate();
  const { showSnackBar } = useSnackBar();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isEdit, setIsEdit] = useState(false);
  const [rowData, setRowData] = useState<EntityFormdata | any>(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [FormToDelete, setFormToDelete] = useState<null | EntityFormdata>(null);
  const [indexToRemove, setIndexToRemove] = useState<number>();
  // const [tableState, setTableState] = useRecoilState(entityTableStateAtom);
  // const fetchedData = useRecoilValueLoadable(fetchEntityFormListSelector);
  const [tableState, setTableState] = useRecoilState(entityFormsStateAtomFamily('uniqueId'));
  const fetchedData = useRecoilValueLoadable(fetchEntityFormListSelector('uniqueId'));

  const [searchQuery, setSearchQuery] = useState(tableState?.filters?.search || '');
  const debouncedSearch = useDebounce(searchQuery, 500);

  const handleEdit = (data: EntityFormdata) => {
    setRowData(data);
    setIsEdit(true);
    setIsModalOpen(true);
  };

  const breadcrumbsData = [
    {
      label: (
        <IconButton color="primary">
          <HomeIcon />
        </IconButton>
      ),
      href: '/',
    },
    { label: 'Datasets' },
  ];

  const handleDelete = (data: EntityFormdata, indexToRemove: number) => {
    setFormToDelete(data);
    setIndexToRemove(indexToRemove);
    setConfirmOpen(true);
  };

  const handleConfirmDelete = async (confirmed: boolean) => {
  if (confirmed && FormToDelete) {
    try {
      let res = await actions.deleteForm(FormToDelete?._id);

      if (res?.status === 200) {
        showSnackBar('Delete Dataset successfully.', 'success');

        const filterKey = generateFilterKey(tableState.filters);

        setTableState((prev) => {
          const updatedList =
            prev.cache[filterKey]?.filter((item) => item._id !== FormToDelete._id) || [];

          return {
            ...prev,
            cache: {
              ...prev.cache,
              [filterKey]: updatedList,
            },
            totalCountCache: {
              ...prev.totalCountCache,
              [filterKey]: (prev.totalCountCache[filterKey] || 1) - 1,
            },
          };
        });
      }
    } catch (error) {
      showSnackBar('Failed to delete dataset. Please try again.', 'error');
    }
  }

  setConfirmOpen(false);
};

  // const handleConfirmDelete = async (confirmed: boolean) => {
  //   if (confirmed && FormToDelete) {
  //     try {
  //       let res = await actions.deleteForm(FormToDelete?._id);
  //       if (res?.status === 200) {
  //         showSnackBar('Delete Dataset successfully.', 'success');
  //         setTableState((prev) => ({
  //           ...prev,
  //           cache: {},
  //           totalCountCache: {},
  //         }));
  //       } else if (res?.status === 400) {
  //         showSnackBar(
  //           res?.response?.data?.data?.general_error ??
  //             'Failed to delete dataset. Please try again.',
  //           'warning'
  //         );
  //       }
  //     } catch (error) {
  //       showSnackBar('Failed to delete dataset. Please try again.', 'error');
  //     }
  //   }
  //   setConfirmOpen(false);
  // };

  useEffect(() => {
    if (fetchedData.state === 'hasValue' && !fetchedData.contents.fromCache) {
      const filterKey = generateFilterKey(tableState.filters);
      setTableState((prev) => ({
        ...prev,
        cache: { ...prev.cache, [filterKey]: fetchedData.contents.data },
        totalCountCache: { ...prev.totalCountCache, [filterKey]: fetchedData.contents.totalCount },
      }));
    }
  }, [fetchedData, setTableState]);

  const handleSearch = (query) => {
    setSearchQuery(query);
  };
  const redirectToDetail = (data: any) => {
    navigate(`/r/datasets/data-details/${data?._id}`);
  };
  useEffect(() => {
    if (debouncedSearch !== tableState.filters.search) {
      setTableState((prev) => ({
        ...prev,
        filters: {
          ...prev.filters,
          search: debouncedSearch,
          page: 1,
        },
        cache: {},
      }));
    }
  }, [debouncedSearch, setTableState]);

  const createFormNavigate = (data: EntityFormdata | null) => {
    navigate('/r/datasets/form-create/', { state: data });
  };

  const columns = useMemo<MRT_ColumnDef<RoleFormData>[]>(
    () => [
      {
        accessorKey: 'index',
        header: '#',
        enableSorting: false,
        // size: 50,
        size: 70,
        // minSize: 60,
        // maxSize: 80,
        Cell: ({ row }) => (tableState.filters.page - 1) * tableState.filters.limit + row.index + 1,
      },
      {
        accessorKey: 'name',
        header: 'Name',
        enableSorting: false,
        // size: 50,
        size: 250,
        minSize: 200,
        Cell: ({ cell }) => <EllipsisWithTooltip text={cell.getValue()} />,
      },
      {
        accessorKey: 'description',
        header: 'Description',
        enableSorting: false,
        // size: 50,
        size: 300,
        minSize: 250,
        Cell: ({ cell }) => <EllipsisWithTooltip text={cell.getValue()} />,
      },
      {
        accessorKey: 'record_count',
        header: 'Record Count',
        enableSorting: false,
        // size: 50,
        size: 120,
        minSize: 100,
      },
      {
        accessorKey: 'edit',
        header: 'Action',
        enableSorting: false,
        // size: 20,
        size: 70,
        minSize: 60,
        maxSize: 80,
        Cell: ({ row }) => {
          const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
          const open = Boolean(anchorEl);

          const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
            setAnchorEl(event.currentTarget);
          };

          const handleClose = () => {
            setAnchorEl(null);
          };

          return (
            <div>
              <IconButton onClick={handleClick}>
                <MoreVertIcon />
              </IconButton>
              <Menu
                id={`menu-${row?.index}`}
                anchorEl={anchorEl}
                open={open}
                onClose={handleClose}
                MenuListProps={{
                  'aria-labelledby': `menu-button-${row.index}`,
                }}
              >
                <MenuItem
                  onClick={() => {
                    handleEdit(row.original);
                    handleClose();
                  }}
                >
                  {' '}
                  <EditIcon fontSize="small" sx={{ mr: 1 }} />
                  Edit
                </MenuItem>
                <MenuItem
                  onClick={() => {
                    createFormNavigate(row?.original);
                    handleClose();
                  }}
                >
                  <NoteAddIcon fontSize="small" sx={{ mr: 1 }} />
                  Update Form
                </MenuItem>
                <MenuItem
                  onClick={() => {
                    redirectToDetail(row.original);
                    handleClose();
                  }}
                >
                  <DescriptionIcon fontSize="small" sx={{ mr: 1 }} />
                  Details
                </MenuItem>
                {row.original.record_count === 0 && (
                  <MenuItem
                    onClick={() => {
                      handleDelete(row.original, row.index);
                      handleClose();
                    }}
                  >
                    <DeleteIcon fontSize="small" sx={{ mr: 1, color: 'error.main' }} />
                    Delete
                  </MenuItem>
                )}
              </Menu>
            </div>
          );
        },
      },
    ],
    [tableState?.filters]
  );

  const onSubmit = async (data: any) => {
    if (rowData?._id) {
      try {
        let res = await actions.updateForm(rowData._id, data);
        if (res?.status === 200) {
          showSnackBar('Dataset updated successfully!', 'success');
          setTableState((prev) => ({
            ...prev,
            cache: {},
            totalCountCache: {},
          }));
        }
      } catch (error) {
        showSnackBar('Failed to update dataset. Please try again.', 'error');
      }
    } else {
      try {
        data.form_fields = staticEntityFormData;
        let res = await actions.createForm(data);
        if (res?.status === 200) {
          showSnackBar('Dataset created successfully!', 'success');
          setTableState((prev) => ({
            ...prev,
            cache: {},
            totalCountCache: {},
          }));
        }
      } catch (error) {
        showSnackBar('Failed to create dataset. Please try again.', 'error');
      }
    }
    setRowData(null);
    setIsModalOpen(false);
  };

  const handleChangePage = (event, newPage) => {
    setTableState((prev) => ({
      ...prev,
      filters: { ...prev.filters, page: newPage + 1 },
    }));
  };

  const handleChangeRowsPerPage = (event) => {
    const value = parseInt(event.target.value) ?? 20;
    setTableState((prev) => ({
      ...prev,
      filters: { ...prev.filters, limit: value, page: 1 },
    }));
  };

  return (
    <Box>
      <DynamicBreadcrumbs
        items={breadcrumbsData}
        onBreadcrumbClick={(event, item: { label: string; href: string }) => {
          if (item) {
            navigate(item?.href);
          }
        }}
      />
      <Grid container spacing={2} sx={{ alignItems: 'center' }}>
        <Grid item xs={6} sx={{ display: 'flex', alignItems: 'center' }}>
          <Typography variant="h4" component="h1" sx={{ fontWeight: 'bold' }}>
            Datasets
          </Typography>
        </Grid>
        <Grid item xs={6} textAlign="right">
          <Button
            variant="contained"
            color="primary"
            size="small"
            onClick={() => {
              setIsModalOpen(true);
              setIsEdit(false);
              setRowData(null);
            }}
          >
            Create
          </Button>
        </Grid>
      </Grid>
      <Grid
        container
        sx={{ alignItems: 'center', paddingTop: '8px', paddingBottom: '8px' }}
        className="search-box-style"
      >
        <Grid item xs={12} md={9} sx={{ display: 'flex', alignItems: 'center' }}>
          <SearchBox handleSearch={handleSearch} value={searchQuery} />
        </Grid>
      </Grid>
      <CommonTable
        columns={columns}
        data={tableState?.cache[generateFilterKey(tableState?.filters)] || []}
        initialState={{
          columnPinning: {
            right: ['mrt-row-actions'],
          },
          density: 'compact',
        }}
        muiTableContainerProps={{ sx: { maxHeight: 'calc(100vh - 260px)', width: '100%' } }}
        state={{ isLoading: fetchedData.state === 'loading' }}
        renderBottomToolbar={() => (
          <Box sx={{ display: 'flex', justifyContent: 'flex-end', padding: 0, margin: 0 }}>
            <Pagination
              totalCount={tableState?.totalCountCache[generateFilterKey(tableState?.filters)] || 0}
              rowsPerPage={tableState?.filters?.limit}
              page={tableState?.filters?.page - 1}
              handleChangePage={handleChangePage}
              handleChangeRowsPerPage={handleChangeRowsPerPage}
            />
          </Box>
        )}
      />
      <ConfirmBox
        open={confirmOpen}
        onClose={handleConfirmDelete}
        options={{
          title: 'Delete Form',
          text: 'Are you sure you want to delete this Form?',
          confirmButtonText: 'Yes',
          cancelButtonText: 'No',
          confirmButtonColor: 'error',
          cancelButtonColor: 'default',
        }}
      />
      <BusinessModal
        title="Dataset"
        open={isModalOpen}
        onClose={() => {
          setIsModalOpen(false);
        }}
        selectedBusiness={rowData}
        handleSubmit={onSubmit}
      />
    </Box>
  );
};
export default List;
